/**
 * Single wiring point for the invoice-generation data provider.
 *
 * Wired to the real backend (Service → API Client → edge function) as of #287
 * Slice 1b. Nothing else in the UI needs to change — components depend only on
 * `InvoiceGenerationProvider`.
 */

import { createRealProvider } from "./realProvider";
import type { InvoiceGenerationProvider } from "./provider";
import { CompanyService } from "@/services/companyService";

// Resolved once per active company and cached — every provider call needs
// companyId, and re-fetching it on every action would be a redundant round
// trip. useSwitchCompany.ts switches company via an in-app SPA navigation (no
// full reload), so the cache MUST be invalidated on switch — see
// resetActiveCompanyCache(), called from useSwitchCompany.ts right after switching.
let companyIdPromise: Promise<string> | null = null;
async function getActiveCompanyId(): Promise<string> {
  if (!companyIdPromise) {
    companyIdPromise = new CompanyService().getCompany().then((c) => c.id);
  }
  return companyIdPromise;
}

export function resetActiveCompanyCache(): void {
  companyIdPromise = null;
}

export const invoiceProvider: InvoiceGenerationProvider = createRealProvider(getActiveCompanyId);

// Shared invoice-generation domain (FIN-688) — types, VAT/totals, draft construction,
// base-on-existing-invoice carry-over, the preview content model, issuing validation,
// and the invoice-presentation rules (due-date arithmetic, dröjsmålsränta, bank-details
// sentence, message cap) all live in `@financialharmony/invoicing` now, shared with
// `apps/client`. Re-exported wholesale so every existing import in `src/` keeps working.
export * from "@financialharmony/invoicing";
export type { InvoiceGenerationProvider } from "./provider";
